import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuthContext } from "@/features/auth/auth-provider";
import { authApi } from "@/lib/auth-api";
import { takePostLoginRedirect } from "@/lib/post-login-redirect";

// Kody z backendu (`login_error_redirect` w routes/google_oauth.rs): callback
// Google przy bledzie wraca tutaj zamiast oddac JSON pod api.farmaster.pl.
const GOOGLE_LOGIN_ERRORS: Record<string, string> = {
	google_domain:
		"To konto Google nie należy do organizacji korzystającej z Farmastera. Zaloguj się kontem służbowym albo e-mailem i hasłem.",
	google_session: "Sesja logowania przez Google wygasła. Spróbuj ponownie.",
	google_cancelled: "Logowanie przez Google zostało anulowane.",
	google_failed: "Nie udało się zalogować przez Google. Spróbuj ponownie.",
	server_error: "Wystąpił błąd serwera podczas logowania. Spróbuj ponownie za chwilę.",
};

export const Route = createFileRoute("/auth/login")({
	validateSearch: (search: Record<string, unknown>): { error?: string } => ({
		error: typeof search.error === "string" ? search.error : undefined,
	}),
	component: LoginPage,
});

function LoginPage() {
	const navigate = useNavigate();
	const { login } = useAuthContext();
	const { error: errorCode } = Route.useSearch();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | null>(() =>
		errorCode ? (GOOGLE_LOGIN_ERRORS[errorCode] ?? GOOGLE_LOGIN_ERRORS.google_failed) : null,
	);
	const [isLoading, setIsLoading] = useState(false);
	const [forgotPassword, setForgotPassword] = useState(false);
	const [resetRequested, setResetRequested] = useState(false);

	// Komunikat zostaje w stanie, kod znika z adresu — odświeżenie nie pokaże
	// starego błędu.
	useEffect(() => {
		if (errorCode) navigate({ to: "/auth/login", search: {}, replace: true });
	}, [errorCode, navigate]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setIsLoading(true);
		try {
			if (forgotPassword) {
				await authApi.requestPasswordReset(email);
				setResetRequested(true);
				return;
			}
			await login({ email, password });
			const next = takePostLoginRedirect();
			if (next) navigate({ href: next });
			else navigate({ to: "/dashboard" });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Błąd logowania");
		} finally {
			setIsLoading(false);
		}
	};

	const handleGoogleLogin = () => {
		setError(null);
		window.location.href = authApi.googleAuthorize();
	};

	const showPasswordReset = () => {
		setError(null);
		setForgotPassword(true);
	};

	const showLogin = () => {
		setError(null);
		setForgotPassword(false);
		setResetRequested(false);
	};

	return (
		<div className="flex min-h-dvh items-start justify-center overflow-y-auto bg-[var(--background)] px-4 py-6 sm:items-center sm:p-6">
			<div className="w-full max-w-sm rounded-lg border border-[var(--border)] bg-[var(--card)] p-6 shadow-sm sm:p-8">
				<div className="mb-6 flex justify-center">
					<img src="/logo2.png" alt="Farmaster" className="h-10 object-contain" />
				</div>
				<h1 className="mb-1 text-xl font-semibold text-[var(--foreground)]">
					{forgotPassword ? "Reset hasła" : "Logowanie"}
				</h1>
				<p className="mb-6 text-sm text-[var(--muted-foreground)]">
					{forgotPassword
						? "Podaj adres e-mail przypisany do konta."
						: "Zaloguj się do systemu Farmaster"}
				</p>
				<form onSubmit={handleSubmit} className="space-y-4">
					<div className="space-y-1.5">
						<label className="text-sm font-medium text-[var(--foreground)]" htmlFor="email">
							Email
						</label>
						<input
							id="email"
							type="email"
							required
							autoComplete="email"
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							disabled={resetRequested}
							placeholder="email@firma.pl"
							className="flex h-9 w-full rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] max-md:h-11 max-md:text-base"
						/>
					</div>
					{!forgotPassword && (
						<div className="space-y-1.5">
							<div className="flex items-center justify-between gap-3">
								<label className="text-sm font-medium text-[var(--foreground)]" htmlFor="password">
									Hasło
								</label>
								<button
									type="button"
									onClick={showPasswordReset}
									className="text-sm text-[var(--primary)] hover:underline max-md:-my-2 max-md:py-2"
								>
									Nie pamiętam hasła
								</button>
							</div>
							<input
								id="password"
								type="password"
								required
								autoComplete="current-password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="••••••••"
								className="flex h-9 w-full rounded-md border border-[var(--input)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)] max-md:h-11 max-md:text-base"
							/>
						</div>
					)}
					{resetRequested && (
						<p className="rounded-md border border-[var(--status-success-border)] bg-[var(--status-success-bg)] px-3 py-2 text-sm text-[var(--status-success-text)]">
							Jeśli konto istnieje, wyślemy e-mail z linkiem do ustawienia nowego hasła.
						</p>
					)}
					{error && (
						<p className="rounded-md border border-[var(--status-error-border)] bg-[var(--status-error-bg)] px-3 py-2 text-sm text-[var(--status-error-text)]">
							{error}
						</p>
					)}
					{!resetRequested && (
						<button
							type="submit"
							disabled={isLoading}
							className="inline-flex h-9 w-full items-center justify-center rounded-md bg-[var(--primary)] px-4 text-sm font-medium text-[var(--primary-foreground)] hover:opacity-90 disabled:opacity-50 max-md:h-11"
						>
							{isLoading
								? forgotPassword
									? "Wysyłanie..."
									: "Logowanie..."
								: forgotPassword
									? "Wyślij link"
									: "Zaloguj się"}
						</button>
					)}
					{forgotPassword && (
						<button
							type="button"
							onClick={showLogin}
							className="inline-flex h-9 w-full items-center justify-center rounded-md border border-[var(--border)] px-4 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--accent)] max-md:h-11"
						>
							Wróć do logowania
						</button>
					)}
					{!forgotPassword && (
						<div className="flex items-center gap-2 py-1">
							<div className="h-px flex-1 bg-[var(--border)]" />
							<span className="text-xs text-[var(--muted-foreground)]">lub</span>
							<div className="h-px flex-1 bg-[var(--border)]" />
						</div>
					)}
					{!forgotPassword && (
						<button
							type="button"
							onClick={handleGoogleLogin}
							className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-[var(--border)] bg-[var(--card)] px-4 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)] max-md:h-11"
						>
							<svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
								<path
									fill="#4285F4"
									d="M23.49 12.27c0-.79-.07-1.54-.21-2.27H12v4.3h6.44a5.5 5.5 0 0 1-2.39 3.61v3h3.86c2.26-2.08 3.58-5.15 3.58-8.64z"
								/>
								<path
									fill="#34A853"
									d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.86-3A7.2 7.2 0 0 1 12 19.26a7.24 7.24 0 0 1-6.8-4.99H1.2v3.14A12 12 0 0 0 12 24z"
								/>
								<path
									fill="#FBBC05"
									d="M5.2 14.27A7.2 7.2 0 0 1 4.8 12c0-.79.14-1.55.4-2.27V6.59H1.2A12 12 0 0 0 0 12c0 1.94.46 3.78 1.2 5.41l4-3.14z"
								/>
								<path
									fill="#EA4335"
									d="M12 4.74c1.76 0 3.35.61 4.6 1.8l3.44-3.44A11.96 11.96 0 0 0 12 0 12 12 0 0 0 1.2 6.59l4 3.14A7.24 7.24 0 0 1 12 4.74z"
								/>
							</svg>
							Kontynuuj z Google
						</button>
					)}
				</form>
			</div>
		</div>
	);
}
